import { pluralize, useAuthConfig } from "@theprelude/core";
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  ScrollBar,
  TrashcanIcon,
} from "@theprelude/ds";
import { z } from "zod";
import { useDeleteSchedules } from "@/hooks/schedule-mutations";
import { useOverlay } from "@/hooks/use-overlay";
import { scheduleQueryOptions } from "@/queries/schedule";
import { useScheduleCallback } from "./schedule-overlay/schedule-overlay";

export const deleteScheduleSchema = z.tuple([
  z.literal("deleteSchedule"),
  z.object({
    type: z.enum(["test", "threat"]),
    ids: z.array(z.string()),
  }),
]);

export type DeleteScheduleSchema = z.infer<typeof deleteScheduleSchema>;

export function DeleteScheduleModal({ ids, type }: DeleteScheduleSchema[1]) {
  const callback = useScheduleCallback();
  const authConfig = useAuthConfig();
  const overlay = useOverlay();
  const scheduleQuery = scheduleQueryOptions(authConfig).useSuspenseQuery();
  const deleteSchedules = useDeleteSchedules();

  const rows =
    type === "test"
      ? scheduleQuery.data["tests"].rows
      : scheduleQuery.data["threats"].rows;

  const selectedRows = rows.filter((row) => ids.includes(row.rowId));

  const uniqueNames = [...new Set(selectedRows.map((row) => row.name))];

  function dialogClose() {
    void overlay.close();
    callback.setValue(undefined);
  }

  return (
    <Dialog defaultOpen onOpenChange={(value) => !value && dialogClose()}>
      <DialogContent className="max-w-[750px]">
        <DialogHeader>
          <DialogTitle>
            {ids.length > 1
              ? `Are you sure you want to remove ${ids.length} ${pluralize(
                  ids.length,
                  type,
                )} from your schedule?`
              : `Are you sure you want to remove this ${type}  from your schedule?`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <span>
            This will disable the following {ids.length > 1 && ids.length + " "}
            {pluralize(ids.length, type)} from running on a scheduled basis:
          </span>
          <ScrollArea className="h-[11rem] py-4">
            {uniqueNames.map((name) => (
              <li key={name}>{name}</li>
            ))}
            <ScrollBar />
          </ScrollArea>
          <span>
            You will have the ability to schedule{" "}
            {ids.length > 1 ? `these ${type}s` : `this ${type}`} again at any
            time.
          </span>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button intent="secondary">Cancel</Button>
          </DialogClose>
          <Button
            intent="negative"
            loading={deleteSchedules.isPending}
            icon={<TrashcanIcon />}
            onClick={() =>
              deleteSchedules.mutate(
                {
                  type,
                  rows: selectedRows.map((row) => {
                    if (
                      !row.isScheduled ||
                      row.target === undefined ||
                      row.runCode === undefined
                    ) {
                      throw new Error(
                        "Target is required for unscheduled rows",
                      );
                    }

                    return {
                      id: row.id,
                      name: row.name,
                      isScheduled: row.isScheduled,
                      target: row.target,
                      runCode: row.runCode,
                    };
                  }),
                },
                {
                  onSuccess: () => {
                    void overlay.close();

                    // This is a workaround to refresh the table after the schedule is updated
                    if (callback.value) {
                      callback.value();
                      callback.setValue(undefined);
                    }
                  },
                },
              )
            }
          >
            Remove schedules
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
